import {
	type CollectionSummaryJSONObject,
	type DuplicateNotification,
	isSystemTag,
	LongRunningTaskStatus,
} from '@ikegps/core-api';
import { Services } from '../../services';
import { AppStore } from '../../store';
import { showDuplicateNotificationDialog } from '../duplicate.notification/duplicate.notification';

export class CollectionDuplicateModalCtrl {
	collections: CollectionSummaryJSONObject[];

	constructor(collections: CollectionSummaryJSONObject[]) {
		this.collections = collections;
	}

	/**
	 * Trigger the server-side long-running duplicate task. Notifications and failures collected
	 * across batches are surfaced in a single follow-up dialog after the task(s) complete.
	 *
	 * The parent `ModalFormComponent` already drives the blocking-spinner UX through `setLoading`,
	 * so this controller doesn't track its own `duplicating` flag.
	 */
	async duplicateCollections(departmentId: string | null): Promise<void> {
		const collectionsByTag: { [tagId: string]: CollectionSummaryJSONObject[] } = {};
		for (const collection of this.collections) {
			const tagId = collection.tags.find((t) => !isSystemTag(t));
			if (collectionsByTag[tagId] != null) {
				collectionsByTag[tagId].push(collection);
			} else {
				collectionsByTag[tagId] = [collection];
			}
		}

		const allNotifications: DuplicateNotification[] = [];
		let hadFailure = false;

		for (const tagId of Object.keys(collectionsByTag)) {
			const collectionGroup = collectionsByTag[tagId];
			if (collectionGroup == null) continue;
			const tag = AppStore.Tag.get(tagId);
			if (tag == null) continue;
			const outcome =
				departmentId == null
					? await Services.Collection.duplicate(collectionGroup, tag.name)
					: await Services.Collection.duplicate(collectionGroup, tag.name, departmentId);
			if (outcome == null) {
				hadFailure = true;
				continue;
			}
			allNotifications.push(...outcome.notifications);
			if (
				outcome.status === LongRunningTaskStatus.COMPLETED_ERRORS ||
				outcome.status === LongRunningTaskStatus.FAILED
			) {
				hadFailure = true;
			}
		}

		if (allNotifications.length > 0 || hadFailure) {
			await showDuplicateNotificationDialog(allNotifications, { hadFailure });
		}
	}
}
